/**
 * Calculates the payout percentages for a given number of places to pay
 * @param {number} placesToPay - Number of places to pay out
 * @param {Array} payoutTable - Array of payout table data
 * @returns {Array} Array of payout percentages for each place
 */
export const calculatePayoutPercentages = (placesToPay, payoutTable) => {
  if (!placesToPay || !payoutTable || payoutTable.length === 0) {
    console.error("Invalid input:", { placesToPay, payoutTable });
    return null;
  }

  // Find the closest payout data that has enough places
  const payoutData = payoutTable
    .filter((data) => data.payoutQuantity >= placesToPay)
    .sort((a, b) => a.payoutQuantity - b.payoutQuantity)[0];

  if (!payoutData) {
    console.error("No payout data found for placesToPay:", placesToPay);
    // If no exact match, use the largest available payout table
    const largestPayout = payoutTable.sort(
      (a, b) => b.payoutQuantity - a.payoutQuantity
    )[0];

    if (!largestPayout) {
      console.error("No payout table entries found");
      return null;
    }

    // Use the largest available payout table and scale it down
    const percentages = payoutTable
      .filter((data) => data.payoutQuantity === largestPayout.payoutQuantity)
      .sort((a, b) => a.payoutOrder - b.payoutOrder)
      .slice(0, placesToPay)
      .map((data) => ({
        place: data.payoutOrder,
        percentage:
          data.percentage * 100 * (placesToPay / largestPayout.payoutQuantity),
      }));

    return percentages;
  }

  // Get the percentages for the number of places we want to pay
  const percentages = payoutTable
    .filter((data) => data.payoutQuantity === payoutData.payoutQuantity)
    .sort((a, b) => a.payoutOrder - b.payoutOrder)
    .slice(0, placesToPay)
    .map((data) => ({
      place: data.payoutOrder,
      percentage: data.percentage * 100, // Convert to percentage
    }));

  return percentages;
};

/**
 * Calculates the number of places to pay based on total entries and payout ratio
 * @param {number} totalEntries - Total number of entries
 * @param {string} ratio - Payout ratio in format "X:Y"
 * @returns {number} Number of places to pay
 */
export const calculatePlacesToPay = (totalEntries, ratio) => {
  if (!ratio || !totalEntries) return 0;

  const [numerator, denominator] = ratio.split(":").map(Number);
  return Math.ceil((totalEntries * numerator) / denominator);
};
